<template>
    <div
        :class="{
            [$style['login']]: true,
            [$style['login_scroll']]: isScroll,
        }"
        @scroll="onScroll"
    >
        <main
            ref="main"
            :class="{
                [$style['login__content']]: true,
                [$style['login__content_top']]: isTopPosition,
            }"
        >
            <div :class="$style['top']">
                <UiButton
                    v-if="showBackToTheSystem"
                    type="button"
                    variant="tertiary"
                    @click="jumpToTheSystem"
                >
                    <UiIcon name="arrowBackward" />
                    {{ t('backToTheSystem') }}
                </UiButton>
                <UiButton
                    v-else-if="isAuthorized"
                    type="button"
                    variant="tertiary"
                    @click="toLogout"
                >
                    <UiIcon name="arrowBackward" />
                    {{ t('logout') }}
                </UiButton>
            </div>
            <div
                :class="{
                    [$style['login__form-wrap']]: true,
                    [$style['login__form-wrap_top']]: isTopPosition,
                }"
            >
                <div v-if="$slots.header" :class="$style['login__header']">
                    <slot name="header" />
                </div>

                <div :class="$style['login__logo']">
                    <img
                        :src="logo"
                        :alt="siteName"
                    />
                </div>

                <h1 :class="$style['login__title']">
                    <slot name="title">
                        {{ t("title", {siteName: siteName}) }}
                    </slot>
                </h1>
                <div :class="$style['login__description']">
                    <slot name="description" />
                </div>
                <div :class="$style['login__form']">
                    <slot />
                </div>
            </div>
        </main>

        <div
            v-if="siteName.toLowerCase() === Theme.RETAILCRM"
            :class="$style['info-panel']"
        >
            <div :class="$style['info-panel__content']">
                <div :class="$style['info-panel__label']">
                    <p>
                        {{
                            releaseNotAvailable
                                ? t("infoPanel.label.before")
                                : t("infoPanel.label.after")
                        }}
                    </p>
                </div>

                <h1 :class="$style['info-panel__title']">
                    {{ t("infoPanel.title") }}
                </h1>

                <p :class="$style['info-panel__description']">
                    {{ t("infoPanel.description.p0") }}<br />
                    {{ t("infoPanel.description.p1") }}
                </p>

                <UiButton
                    :class="$style['info-panel__button']"
                    href="https://retailcrm.ru/v11"
                    target="_blank"
                    size="md"
                >
                    {{
                        releaseNotAvailable
                            ? t("infoPanel.action.willWatchRelease")
                            : t("infoPanel.action.learnMore")
                    }}
                </UiButton>
            </div>

            <img
                :class="$style['info-panel__image']"
                :src="preview.src"
                :srcset="preview.srcset"
                draggable="false"
                loading="lazy"
                alt=""
            />
        </div>

        <img
            v-else
            :src="backgroundImageUrl"
            :class="$style['login__image']"
            draggable="false"
            loading="lazy"
            alt=""
        />
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { defineEmits } from 'vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMeStore } from '@/stores/me';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { useTheme } from '@/composables/theme';

import { LOCALES } from '@/i18n';
import { Theme } from '@/composables/theme';

import LoginImageRu from '@/assets/img/login__decor_ru.svg';
import LoginImageEn from '@/assets/img/login__decor_en.svg';
import LoginImageEs from '@/assets/img/login__decor_es.svg';

import ReleaseBackgroundBefore1x from '@/assets/img/release-background-21.05@1x.webp'
import ReleaseBackgroundBefore2x from '@/assets/img/release-background-21.05@2x.webp'

import ReleaseBackgroundAfter1x from '@/assets/img/release-background-22.05@1x.webp'
import ReleaseBackgroundAfter2x from '@/assets/img/release-background-22.05@2x.webp'

interface ScrollEvent {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

interface ImageSource {
  src: string,
  srcset: string,
}

defineProps({
    isTopPosition: {
        type: Boolean,
        default: false,
    },
    isScroll: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits<(e: 'scroll', data: ScrollEvent) => void>()

const { t, locale } = useI18n();
const { logo, siteName } = useTheme();
const route = useRoute();
const router = useRouter();

const { isAuthorized } = useMeStore();

const main = ref<HTMLElement | null>(null);

const releaseNotAvailable = false
const preview = computed((): ImageSource => {
    return releaseNotAvailable
        ? {
            src: ReleaseBackgroundBefore1x,
            srcset: `${ReleaseBackgroundBefore1x} 1x, ${ReleaseBackgroundBefore2x} 2x`,
        }
        : {
            src: ReleaseBackgroundAfter1x,
            srcset: `${ReleaseBackgroundAfter1x} 1x, ${ReleaseBackgroundAfter2x} 2x`,
        }
})

const backgroundImageUrl = computed(() => {
    switch (locale.value) {
        case LOCALES.EN:
            return LoginImageEn;
        case LOCALES.ES:
            return LoginImageEs;
        default:
            return LoginImageRu;
    }
});

const toLogout = () => router.push({ name: 'logout', query: route.query })

const onScroll = (): void => {
    if (!main.value) {
        return
    }

    emit('scroll', {
        scrollTop: main.value.scrollTop,
        clientHeight: main.value.clientHeight,
        scrollHeight: main.value.scrollHeight,
    })
}

const showBackToTheSystem = !!route.query.ref

const jumpToTheSystem = () => {
    if (!showBackToTheSystem) {
        return
    }

    location.href = String(route.query.ref)
}
</script>

<style lang="less" module>
@import (reference) "../../assets/styles/variables.less";

@imgWidth: calc(100vh * (1001 / 1223));

.login {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  height: 100%;
  overflow-y: auto;
  background-color: #fff;
  font-family: @font-family-default;
  line-height: 1.5;
  color: @black-500;

  &_scroll {
    overflow-y: scroll;
  }

  &__image {
    height: 100%;
    width: auto;
    min-width: 544px;
    object-fit: cover;
    object-position: left;
  }

  &__content {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #fff;
    flex: 1;
    padding: @spacing-xl;
    position: relative;

    &_top {
        align-items: flex-start;
    }
  }

  &__inner-wrap {
    height: 100%;
    position: relative;
  }

  &__logo {
    width: 56px;
    height: 56px;
    margin: 0 auto;
  }

  &__title {
    font: 800 40px/44px @font-family-Gilroy;
    text-align: center;
    margin-top: @spacing-m;
    margin-bottom: 0;
  }

  &__description {
    text-align: center;
    margin-top: 6px;
  }

  &__form {
    margin: @spacing-l auto 0;
    max-width: 352px;
  }

  &__form-wrap {
    width: 400px;
    margin: auto;

    &_top {
        margin-top: 0;
    }

    @media screen and (max-width: @phone) {
        width: 100%;
        max-width: 400px;
    }
  }

  &__header {
    margin-bottom: 60px;
  }
}

.top {
    display: flex;
    align-items: flex-start;
    width: 100%
}

@media screen and (max-width: @tablet) {
  .login {
    display: block;

    &__image {
      display: none;
    }

    &__content {
      padding: @spacing-s !important;
      margin: auto;
      background: none !important;
    }

    &__form-wrap {
      margin: auto;
    }
  }
}

.info-panel {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  width: auto;
  min-height: fit-content;
  flex: 2;
  padding: 30px;
  background-color: #060C25;
  overflow: hidden;

  @media screen and (max-width: @tablet) {
    height: 594px;
  }

  @media screen and (max-width: @phone) {
    padding: 30px @spacing-xs;
  }

  @media screen and (max-width: 460px) {
    display: flex;
    flex-direction: column;
    padding: 0;
    align-items: center;
    min-width: fit-content;
    min-height: 467px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
    position: relative;
    color: #FFFFFF;
    font-family: @font-family-default;
    z-index: 10000;

    @media screen and (max-width: 460px) {
      padding: 40px 12px 28px 12px;
    }
  }

  &__image {
    height: 100%;
    width: 100%;
    position: absolute;
    object-fit: cover;
    object-position: center;

    @media screen and (max-width: 460px) {
      position: relative;
    }
  }

  &__label {
    padding: 6px;
    border: 1px solid #0061EE;
    border-radius: @spacing-l;
    background: rgba(0, 0, 0, 0.4);

    & > p {
      height: 22px;
      background: linear-gradient(74.85deg, #00DDD9 0.71%, #0068FF 93.03%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      .font-body;
      letter-spacing: 2px;

      &::before,
      &::after {
        content: "";
        display: inline-block;
        width: 6px;
        height: 6px;
        background-color: #00DDD9;
        border-radius: 40px;
        margin: 0 6px 3px 6px;
        vertical-align: middle;
      }

      &::after {
        background-color: #016FFD;
      }
    }
  }

  &__title {
    margin-top: 12px;
    .base-font(600, 40px, 110%);
    font-family: @font-family-Gilroy;

    @media screen and (max-width: 460px) {
      margin-top: @spacing-xs;
      font-size: 28px;
    }
  }

  &__description {
    margin-top: 12px;
    .base-font(400, 22px, 120%);

    @media screen and (max-width: @tablet) {
      font-size: 20px;
    }

    @media screen and (max-width: 460px) {
      margin-top: @spacing-xs;
      font-size: 14px;
    }
  }

  &__button {
    --omnica-button-background-color: #00DDD9;
    --omnica-button-background-color-active: linear-gradient(74.85deg, #00C0BC 0.71%, #0055D1 93.03%);
    --omnica-button-background-image: linear-gradient(74.85deg, #00DDD9 0.71%, #0068FF 93.03%);

    margin-top: @spacing-m;

    &:hover,
    &:active {
      --omnica-button-background-color: #00C0BC;
      --omnica-button-background-image: linear-gradient(74.85deg, #00C0BC 0.71%, #0055D1 93.03%);

      background-image: var(--omnica-button-background-image);
    }
  }
}
</style>

<i18n locale="ru_RU" lang="json">
{
  "title": "Вход в {siteName}",
  "backToTheSystem": "Вернуться в систему",
  "logout": "Выйти",
  "infoPanel": {
    "title": "Новая RetailCRM 11",
    "description": {
      "p0": "Больше возможностей",
      "p1": "для CRM-маркетинга и роста продаж"
    },
    "label": {
      "before": "21 МАЯ",
      "after": "УЖЕ ДОСТУПНА"
    },
    "action": {
      "willWatchRelease": "Буду смотреть релиз",
      "learnMore": "Узнать подробнее"
    }
  }
}
</i18n>

<i18n locale="en_GB" lang="json">
{
  "title": "Log in {siteName}",
  "backToTheSystem": "Back to system",
  "logout": "Sign out",
  "infoPanel": {
    "title": "New RetailCRM 11",
    "description": {
      "p0": "More tools for CRM marketing and sales growth",
      "p1": ""
    },
    "label": {
      "before": "MAY 21",
      "after": "NOW AVAILABLE"
    },
    "action": {
      "willWatchRelease": "I’ll watch the release",
      "learnMore": "Learn more"
    }
  }
}
</i18n>

<i18n locale="es_ES" lang="json">
{
  "title": "Entrar a {siteName}",
  "backToTheSystem": "Volver al sistema",
  "logout": "Cerrar sesión",
  "infoPanel": {
    "title": "Nueva RetailCRM 11",
    "description": {
      "p0": "Más posibilidadespara el marketing CRM",
      "p1": "y el crecimiento de ventas"
    },
    "label": {
      "before": "21 DE MAYO",
      "after": "YA DISPONIBLE"
    },
    "action": {
      "willWatchRelease": "Voy a ver el lanzamiento",
      "learnMore": "Saber más"
    }
  }
}
</i18n>
